import { useSessionStorageState } from 'ahooks';
import type { PublicUser } from '~/routes/remix-api.user.public-user';
import {
    EMAIL_INPUT,
    FIRST_NAME_INPUT,
    LAST_NAME_INPUT,
    PASSWORD_INPUT,
    PHONE_NUMBER_INPUT,
    REMEMBER_ME_INPUT,
} from '~/modules/validation/util/form';
import { z } from 'zod';

export type LoginFlow =
    | 'login'
    | 'sign-up'
    | 'post-enquiry-sign-up'
    | 'post-enquiry-login'
    | 'forgot-password'
    | 'forgot-password-completed'
    | null;

export const loginValidator = z.object({
    email: EMAIL_INPUT,
    password: PASSWORD_INPUT,
    rememberMe: REMEMBER_ME_INPUT,
});

export const signUpValidator = loginValidator.extend({
    firstName: FIRST_NAME_INPUT,
    lastName: LAST_NAME_INPUT,
    phone: z.string().optional(),
});

/** HDE sign-up validator with optional phone - empty string is treated as absent */
export const hdeSignUpValidator = signUpValidator
    .extend({
        phone: z.preprocess(
            (val) => (val === '' || val === undefined ? undefined : val),
            PHONE_NUMBER_INPUT.optional(),
        ),
    })
    .superRefine((data, ctx) => {
        const tokens = `${data.firstName.trim()} ${data.lastName.trim()}`
            .trim()
            .split(/\s+/)
            .filter(Boolean);
        if (tokens.length > 2) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                    'Please enter only your first and last name (no middle name).',
                path: ['lastName'],
            });
        }
    });

/**
 * HDE homeowner step before login vs sign-up is known: only email (and consent) is collected.
 * Continue must pass validation without password so we can run the public-user lookup.
 */
export const hdeEmailDiscoveryValidator = z.object({
    email: EMAIL_INPUT,
    password: z.string().optional(),
    rememberMe: REMEMBER_ME_INPUT,
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    phone: z.string().optional(),
});

interface LoginFormSegments {
    emailSegment?: boolean;
    passwordSegment?: boolean;
    firstNameSegment?: boolean;
    lastNameSegment?: boolean;
    rememberMeSegment?: boolean;
}

const POST_SIGNUP_NAVIGATION_KEY = 'post-signup-navigation';

export const useLoginFlow = (flow: LoginFlow) => {
    const [navigatePostSignUp, setPostNavigationSignUp] =
        useSessionStorageState<boolean>(POST_SIGNUP_NAVIGATION_KEY);

    const defaultFormValues = (
        enquirySession?: { email?: string; name?: string },
        publicUser?: PublicUser,
    ) => {
        const defaultPostEnquiryValues = {
            email: enquirySession?.email ?? publicUser?.email,
            password: undefined,
            name: enquirySession?.name,
            rememberMe: 'true',
        };
        switch (flow) {
            case 'post-enquiry-sign-up':
            case 'post-enquiry-login':
                const [firstName, ...rest] =
                    defaultPostEnquiryValues.name?.split(' ') ?? '';
                const lastName = rest.join(' ');
                return {
                    email: defaultPostEnquiryValues?.email,
                    password: undefined,
                    firstName: firstName,
                    lastName: lastName,
                    rememberMe: 'true',
                };
            case 'sign-up':
                return {
                    email: publicUser?.email,
                    firstName: publicUser?.firstName,
                    lastName: publicUser?.lastName,
                    rememberMe: 'true',
                };
            default:
                return {
                    email: publicUser?.email,
                    rememberMe: 'true',
                };
        }
    };

    const getTitle = (): string => {
        switch (flow) {
            case 'forgot-password':
            case 'forgot-password-completed':
                return 'Reset password link sent';
            case 'post-enquiry-sign-up':
            case 'sign-up':
                return 'Sign up';
            case 'post-enquiry-login':
            case 'login':
                return 'Log in';
            default:
                return 'Log in or sign up';
        }
    };

    const formValidator = () => {
        switch (flow) {
            case 'post-enquiry-sign-up':
            case 'sign-up':
                return signUpValidator;
            case 'post-enquiry-login':
            case 'login':
            default:
                return loginValidator;
        }
    };

    const displayFormSegments = (): LoginFormSegments => {
        switch (flow) {
            case 'sign-up':
                return {
                    firstNameSegment: true,
                    lastNameSegment: true,
                    emailSegment: true,
                    passwordSegment: true,
                };
            case undefined:
            case null:
                return {
                    emailSegment: true,
                    passwordSegment: false,
                };
            default:
                return {
                    emailSegment: true,
                    passwordSegment: true,
                };
        }
    };

    const submitButtonText = () => {
        switch (flow) {
            case 'post-enquiry-login':
            case 'login':
                return 'Login';
            default:
                return 'Continue';
        }
    };

    return {
        title: getTitle(),
        defaultFormValues,
        formValidator,
        displayFormSegments: displayFormSegments(),
        submitButtonText,
        navigatePostSignUp,
        setPostNavigationSignUp,
    };
};
